import _ from 'lodash';
import moment, { Moment } from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import { useFeature } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Checkbox,
	Input,
	Label,
	Paragraph,
	Select,
	Separator,
	Subheading,
} from '@treams/treams-ui';

const useMomentJs = () => {
	/**
	 * Takes in a time and modifies it to the given interval.
	 * taken from https://github.com/moment/moment/issues/959#issuecomment-301767843
	 * @param interval - Nearest chosen interval E.G. 5,10,15,30,etc... minutes
	 * @param time - Time as MomentJS object
	 * @returns - MomentJs object with the nearest interval
	 */
	const nearestMinutes = (interval: number, time: Moment): Moment => {
		const roundedMinutes = Math.round(time.clone().minute() / interval) * interval;
		return time.clone().minute(roundedMinutes).second(0);
	};

	return {
		nearestMinutes,
	};
};

function ConversationMeetingPlanner(props: {
	checkbox?: boolean;
	selected: boolean;
	onSelect: (selected: boolean) => void;
	startTime?: Date;
	duration: number;
	location: string;
	onChange: (startTime: Date, duration: number, location: string) => void;
}) {
	const { nearestMinutes } = useMomentJs();
	const { t } = useTranslation('', { keyPrefix: 'evaluationSubmissions.create.meetingPlanner' });

	/* Value definitions */
	const endRef = useRef<null | HTMLDivElement>(null);

	const time = nearestMinutes(15, moment(props.startTime));
	const timeLabel = time.format('HH:mm');

	const currentDay = moment(props.startTime).startOf('day');

	const options = useMemo(
		() =>
			_.range(8, 19).flatMap((hour) => {
				const time = currentDay.set({ hour: hour });

				return [0, 15, 30, 45].map((minutes) => {
					time.set({ minutes: minutes });
					return {
						value: time.toDate().toString(),
						title: time.format('HH:mm').toString(),
					};
				});
			}),
		[currentDay],
	);

	const minutesLabel = t('duration.minutes');
	const hourLabel = t('duration.hour');
	const meetingDurationOptions = [
		{
			title: `15 ${minutesLabel}`,
			value: '15',
		},
		{
			title: `30 ${minutesLabel}`,
			value: '30',
		},
		{
			title: `45 ${minutesLabel}`,
			value: '45',
		},
		{
			title: `1 ${hourLabel}`,
			value: '60',
		},
		{
			title: `1 ${hourLabel} & 15 ${minutesLabel}`,
			value: '75',
		},
		{
			title: `1 ${hourLabel} & 30 ${minutesLabel}`,
			value: '90',
		},
	].reverse();

	const scrollToBottom = () => {
		endRef.current?.scrollIntoView({ behavior: 'smooth' });
	};

	const handleChange = (duration: number, location: string, value?: string) => {
		if (value) {
			props.onChange(new Date(value), duration, location);
		}
	};

	if (props.selected) {
		scrollToBottom();
	}

	return (
		<>
			<Flex>
				{props.checkbox !== false && (
					<Box flexGrow={0} flexShrink={0} mr={2}>
						<Checkbox checked={props.selected} onClick={() => props.onSelect(!props.selected)} />
					</Box>
				)}
				<Box flexGrow={1} flexShrink={1}>
					{props.checkbox !== false && (
						<Paragraph onClick={() => props.onSelect(true)} mb={3}>
							{t('description')}
						</Paragraph>
					)}
					{props.selected && (
						<>
							<Separator mb={3} />
							<Flex mb={3}>
								<Box mr={2}>
									<Label mb={1}>{t('startTime.label')}:</Label>
									<Select
										selectedOption={{
											value: props.startTime?.toString(),
											title: timeLabel,
										}}
										onChange={(value) => handleChange(props.duration, props.location, value)}
										options={options}
									/>
								</Box>
								<Box ml={2}>
									<Label mb={1}>{t('duration.label')}:</Label>
									<Select
										selectedValue={String(props.duration)}
										onChange={(value) =>
											handleChange(Number(value), props.location, props.startTime?.toString())
										}
										options={meetingDurationOptions}
									/>
								</Box>
							</Flex>
							<Flex flexDirection="column">
								<Label mb={1}>{t('location.label')}</Label>
								<Input
									value={props.location}
									placeholder={t('location.placeholder')}
									onChange={(value) =>
										handleChange(props.duration, value, props.startTime?.toString())
									}
								/>
							</Flex>
						</>
					)}
				</Box>
			</Flex>
			<div ref={endRef} />
		</>
	);
}

export function ConversationDateSelect(props: {
	invite?: boolean;
	value?: { deadline?: Date; meetingDuration?: number; location: string };
	onChange: (value: {
		deadline: Date | undefined;
		meetingDuration?: number;
		location: string;
	}) => void;
	titlePrefix?: string;
}) {
	const { t } = useTranslation('', { keyPrefix: 'evaluations.create.modal.dueDate' });
	const hasConversationPlanning = useFeature(graphql.FeatureName.ConversationPlanning);

	const [deadline, setDeadline] = useState(props.value?.deadline);
	const [meetingDuration, setMeetingDuration] = useState(props.value?.meetingDuration ?? 60);
	const [location, setLocation] = useState(props.value?.location ?? '');
	const [startTime, setStartTime] = useState(
		props.value?.deadline ? props.value.deadline : moment().set({ hour: 9 }).toDate(),
	);

	const [invite, setInvite] = useState(props.invite ?? false);

	const handleMeetingPlanner = (startTime: Date, duration: number, location: string) => {
		setStartTime(startTime);
		setMeetingDuration(duration);
		setDeadline(startTime);
		setLocation(location);
	};

	const handleDatePicker = (deadline: Date | undefined) => {
		if (deadline) {
			setDeadline(deadline);
			setStartTime(deadline);
		}
	};

	useEffect(() => {
		props.onChange({
			deadline: startTime,
			meetingDuration: invite ? meetingDuration : undefined,
			location: location,
		});
	}, [deadline, startTime, meetingDuration, location, invite, props]);

	return (
		<>
			<Subheading>
				{props.titlePrefix ? `${props.titlePrefix} ` : ''}
				{t(hasConversationPlanning ? 'label' : 'deadlineLabel')}
			</Subheading>
			<Paragraph mb={3}>
				{t(hasConversationPlanning ? 'description' : 'deadlineDescription')}
			</Paragraph>
			<Label mb={2}>{t(hasConversationPlanning ? 'label' : 'deadlineLabel')}</Label>
			<Flex>
				<Input.DatePicker
					value={props.value?.deadline}
					onChange={(deadline) => handleDatePicker(deadline)}
				/>
			</Flex>

			{hasConversationPlanning && (
				<Box mt={3}>
					<ConversationMeetingPlanner
						checkbox={typeof props.invite !== 'boolean'}
						selected={invite}
						onSelect={setInvite}
						startTime={startTime}
						duration={meetingDuration}
						location={location}
						onChange={(startTime, duration, location) =>
							handleMeetingPlanner(startTime, duration, location)
						}
					/>
				</Box>
			)}
		</>
	);
}
